import React, { useContext, useEffect } from 'react';
import {
  IDENTITIES_TABLE_NAME_LINK,
  POLICY_LIBRARY_SEVERITY_FILTER,
  SAST_MAIN_TABLE,
  SAST_PAGE_HEADER,
  SAST_SCAN_CSV_DOWNLOAD,
} from '../../constants/test-ids';
import { PageHeader } from '../../components/page-components/page-header/page-header';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  ExtraCellsObject,
  TableComponent,
} from '../../components/table/table-component/table-component';
import { useGetResponseDataWithPagination } from '../../hooks/use-get-response-data-with-pagination';
import { ApiContext } from '../../components/providers/api-provider/api-provider';
import { tableStructure } from './constants';
import { Colors } from '../../constants/colors';
import { FontSizes } from '../../constants/font-sizes';
import moment from 'moment/moment';
import dayjs from 'dayjs';
import { TableCellWithLink } from 'components/table/table-components/table-components';
import { useNavigate } from 'react-router-dom';
import { paths } from 'constants/routes';
import {
  RightTableFiltersContainer,
  TableFiltersContainer,
} from 'components/table/styles';
import { SeverityFilter } from 'pages/alerts/components/severity-filter/severity-filter';
import { useDispatch } from 'react-redux';
import { FiltersTableName } from 'store/modules/filters/filters.reducer';
import { RenderSourceCell } from 'components/info-component/info-component';
import { StyledButton } from 'components/button/button';
import { FilterChips } from 'components/table/table-filters-components/filter-chips/chips-list';
import {
  setApplicableFilters,
  setColumnCoordinates,
  setSelectedColumn,
  setSelectedTable,
} from 'store/modules/selected-column/selected-column.reducer';
import { useSelectedColumnSelector } from 'store/modules/selected-column/selected-column.selector';
import { CsvDownload } from 'components/table/table-filters-components/csv-download/csv-download';
import { setIdentitiesDetailsTab } from 'store/modules/identities/identities.reducer';

const mapSeverityToText: any = {
  '2': 'low',
  '3': 'medium',
  '4': 'high',
  '5': 'critical',
};

export const SastScan = () => {
  const apiContext = useContext(ApiContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tableId = FiltersTableName.SAST_FINDINGS_TABLE;
  const {
    applicableFilters: tableFilters,
    activeSortedField,
    activeSortedOrder,
    searchBy,
  } = useSelectedColumnSelector(tableId);

  const [selectedSeverity, setSelectedSeverity] = React.useState(
    tableFilters?.severity?.toString() || 'all',
  );

  const handleSeverityDropdownChange = (event: SelectChangeEvent) => {
    const severity = event.target.value;
    setSelectedSeverity(severity);
    dispatch(
      setApplicableFilters({
        table: tableId,
        applicableFilters: severity !== 'all' ? { severity: severity } : {},
      }),
    );
  };
  useEffect(() => {
    dispatch(setSelectedTable({ table: tableId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useLazyGetSastScanDataQuery =
    apiContext.baseApi?.useLazyGetSastScanDataQuery;

  const [open, setOpen] = React.useState(false);
  const [stateData, setStateData] = React.useState<any>(null);

  const handleOpen = (item: any) => {
    setStateData(item);
    setOpen(true);
  };

  const handleClose = () => {
    setStateData(null);
    setOpen(false);
  };

  const {
    dataList: sastScanList,
    isLoading: isSastDataLoading,
    isError: isSastDataErrored,
    isFetching: isSastDataFetching,
    isNoFindings: isEmptySastData,
    total,
  } = useGetResponseDataWithPagination({
    baseApiCallback: useLazyGetSastScanDataQuery,
    itemsPerPage: 10,
    tableFilters,
    severity:
      selectedSeverity === 'all'
        ? undefined
        : mapSeverityToText[selectedSeverity],
    sortBy: `${activeSortedOrder}${activeSortedField}`,
    tableId,
    searchBy,
  });

  const dialogContent = (item: any) => (
    <>
      {stateData?.message && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Message:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.message}
          </Typography>
        </Box>
      )}
      {stateData?.branch && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Branch:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.branch}
          </Typography>
        </Box>
      )}
      {stateData?.start_line && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Start Line:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.start_line}
          </Typography>
        </Box>
      )}
      {stateData?.end_line && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            End Line:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.end_line}
          </Typography>
        </Box>
      )}
      {stateData?.sha && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            sha:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.sha}
          </Typography>
        </Box>
      )}
      {stateData?.author_email && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Author Email:{' '}
          </Typography>
          <Typography
            color={Colors.binge}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              dispatch(setIdentitiesDetailsTab(0));
              navigate(
                `${paths.identityDetails}?user=${stateData?.author_email}${
                  item.source ? '&source=' + item.source : ''
                }`,
              );
            }}
          >
            {stateData?.author_email}
          </Typography>
        </Box>
      )}
    </>
  );

  const dialogContentDismissed = (item: any) => (
    <>
      {stateData?.dismissed_by && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Dismissed By:{' '}
          </Typography>
          <Typography
            color={Colors.binge}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              dispatch(setIdentitiesDetailsTab(0));
              navigate(
                `${paths.identityDetails}?user=${stateData?.dismissed_by}${
                  item.source ? '&source=' + item.source : ''
                }`,
              );
            }}
          >
            {stateData?.dismissed_by}
          </Typography>
        </Box>
      )}
      {stateData?.dismissed_at && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Dismissed Date:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {moment(stateData?.dismissed_at).format('MMM DD, YYYY - LT')}
          </Typography>
        </Box>
      )}
      {stateData?.dismissed_reason && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Reason:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.dismissed_reason}
          </Typography>
        </Box>
      )}
      {stateData?.dismissed_comment && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Comment:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.dismissed_comment}
          </Typography>
        </Box>
      )}
    </>
  );

  const dialogContentFixed = (item: any) => (
    <>
      {dialogContentDismissed(item)}
      {stateData?.fixed_at && (
        <Box
          sx={{
            display: 'flex',
            height: '60px',
          }}
        >
          <Typography
            color={Colors.darkGray}
            fontSize={FontSizes.fontSize16}
            fontWeight={500}
            sx={{ minWidth: '150px' }}
          >
            Fixed Date:{' '}
          </Typography>
          <Typography
            color={Colors.blackPearl}
            fontSize={FontSizes.fontSize16}
            fontWeight={600}
          >
            {stateData?.fixed_at}
          </Typography>
        </Box>
      )}
    </>
  );

  const RenderStateCell = (item: any) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          sx={{ color: Colors.binge, cursor: 'pointer' }}
          onClick={() => handleOpen(item)}
        >
          {item.state.charAt(0).toUpperCase() + item.state.substr(1)}
        </Typography>
        <Dialog
          open={open}
          onClose={handleClose}
          hideBackdrop
          sx={{
            '& .MuiDialog-paper': {
              background: Colors.white,
              width: '70vw',
              maxWidth: '80vw',
            },
          }}
        >
          <DialogContent>
            <Box sx={{ p: 2 }}>
              {dialogContent(item)}
              {stateData?.state === 'dismissed' && dialogContentDismissed(item)}
              {stateData?.state === 'fixed' && dialogContentFixed(item)}
            </Box>
          </DialogContent>
          <DialogActions>
            <StyledButton
              onClick={handleClose}
              color="secondary"
              variant="outlined"
              size="large"
            >
              Close
            </StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const RenderCreatedAt = (item: any) => {
    return dayjs(item.created_at).format('L LT');
  };

  const RenderUpdatedAt = (item: any) => {
    return dayjs(item.updated_at).format('L LT');
  };

  const RenderAuthorLoginCell = (item: any) => {
    return (
      <>
        {item?.author_email ? (
          <TableCellWithLink
            handleClick={() => {
              handleClose();
              dispatch(setIdentitiesDetailsTab(0));
              navigate(
                `${paths.identityDetails}?user=${item?.author_email}${
                  item.source ? '&source=' + item.source : ''
                }`,
              );
            }}
            isActive={true}
            itemTitle={item.author_email}
            align="left"
            dataTestId={IDENTITIES_TABLE_NAME_LINK}
          />
        ) : (
          '-'
        )}
      </>
    );
  };

  const renderSourceCell = (item: any) => {
    return item?.source && <RenderSourceCell source={item?.source} />;
  };

  const filtersForCSV = (tableFilters: any) => {
    if (tableFilters.severity) {
      return {
        ...tableFilters,
        severity: mapSeverityToText[tableFilters.severity],
      };
    }

    return { ...tableFilters };
  };

  const formatSearchBy = (searchBy: any[]) => {
    const formattedSearchBy: any[] = [];
    searchBy.forEach((searchByObj) => {
      if (searchByObj.id === 'owner') {
        formattedSearchBy.push({ ...searchByObj, id: 'org' });
      } else if (searchByObj.id === 'author_email') {
        formattedSearchBy.push({ ...searchByObj, id: 'user' });
      } else if (searchByObj.id === 'repository') {
        formattedSearchBy.push({ ...searchByObj, id: 'repo' });
      } else {
        formattedSearchBy.push({ ...searchByObj });
      }
    });

    return formattedSearchBy;
  };

  const SastExtraCell: ExtraCellsObject = {
    causeMetadata: RenderStateCell,
    author_email: RenderAuthorLoginCell,
    created_at: RenderCreatedAt,
    updated_at: RenderUpdatedAt,
    source: renderSourceCell,
  };

  return (
    <Box>
      <PageHeader title="SAST Scan" dataTestId={SAST_PAGE_HEADER} />
      <TableFiltersContainer>
        {' '}
        <RightTableFiltersContainer>
          <FilterChips tableId={tableId} />
          <SeverityFilter
            value={selectedSeverity}
            onChange={handleSeverityDropdownChange}
            dataTestId={POLICY_LIBRARY_SEVERITY_FILTER}
            // showInformational={true}
            useMedium={true}
          />
          <CsvDownload
            total={total}
            activeSortedField={activeSortedField}
            activeSortedOrder={activeSortedOrder}
            apiUrl="findings/sast/detections"
            fileName="Sast Scan Finding"
            tableFilters={filtersForCSV(tableFilters)}
            dataTestId={SAST_SCAN_CSV_DOWNLOAD}
            searchBy={
              searchBy && searchBy.length > 0 ? formatSearchBy(searchBy) : null
            }
          />
        </RightTableFiltersContainer>
      </TableFiltersContainer>
      <TableComponent
        tableId={tableId}
        isListLoading={isSastDataLoading}
        isListErrored={isSastDataErrored}
        isEmptyList={isEmptySastData}
        isListFetching={isSastDataFetching}
        dataTestId={SAST_MAIN_TABLE}
        list={sastScanList}
        tableColumns={tableStructure}
        extraCellsRenderObj={SastExtraCell}
        tableMinHeight="300px"
        columnFilterClickHandler={(column: any, coordinates?: any) => {
          dispatch(setSelectedColumn({ table: tableId, column: column?.id }));
          dispatch(setColumnCoordinates({ coordinates, table: tableId }));
        }}
      />
    </Box>
  );
};
